import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackwardStep, faBackward, faChevronLeft, faChevronRight, faForward, faForwardStep } from '@fortawesome/free-solid-svg-icons';
import { api } from '../services/api';
import { moves2sgf, downloadSgf } from '../services/sgf';
import { computeBoardState } from '../services/boardUtils';
import { sgfToPoint, formatScore } from '../services/coordinateUtils';
import GoBoard from './GoBoard';
import WinrateChart from './WinrateChart';
import type { WinratePoint } from './WinrateChart';
import type { BoardMark, Move } from '../types/game';
import './BattlePage.css';

type Engine       = 'deepgo' | 'katagui';
type KataguiModel = 'guest' | 'pro';
type Status       = 'idle' | 'running' | 'paused' | 'finished' | 'error';

interface BattleMove {
  num: number;
  color: 'black' | 'white';
  engine: string;
  move: string;
  score: number | null;
  winprob: number | null;
  visits: number | null;
}

interface BattleState {
  id: string;
  config: { blackEngine: Engine; kataguiModel: KataguiModel; boardSize: number; komi: number };
  status: Status;
  moves: BattleMove[];
  thinking: string | null;
  result: string | null;
  error: string | null;
}

const engineLabel = (e: Engine, model: KataguiModel) =>
  e === 'deepgo' ? 'DeepGo' : `KataGUI ${model === 'pro' ? 'Pro(b18)' : 'Guest(b10)'}`;

// ── Setup modal ───────────────────────────────────────────────────────────────

interface SetupModalProps {
  onStart: (cfg: { blackEngine: Engine; kataguiModel: KataguiModel; boardSize: number; komi: number }) => void;
  onCancel?: () => void;
  canCancel: boolean;
}

function SetupModal({ onStart, onCancel, canCancel }: SetupModalProps) {
  const [blackEngine,  setBlackEngine]  = useState<Engine>('deepgo');
  const [kataguiModel, setKataguiModel] = useState<KataguiModel>('guest');
  const [boardSize,    setBoardSize]    = useState(19);
  const [komi,         setKomi]         = useState(6.5);

  const whiteEngine: Engine = blackEngine === 'deepgo' ? 'katagui' : 'deepgo';

  return (
    <div className="battle-modal-backdrop" onClick={canCancel ? onCancel : undefined}>
      <div className="battle-modal" onClick={e => e.stopPropagation()}>
        <div className="battle-modal-header">
          <h2>New Battle</h2>
          {canCancel && (
            <button className="battle-modal-close" onClick={onCancel}>✕</button>
          )}
        </div>

        <div className="battle-setup-grid">
          <div className="battle-setup-section">
            <h3>Black</h3>
            <div className="battle-engine-toggle">
              <button className={`battle-engine-btn${blackEngine === 'deepgo'  ? ' active' : ''}`} onClick={() => setBlackEngine('deepgo')}>DeepGo</button>
              <button className={`battle-engine-btn${blackEngine === 'katagui' ? ' active' : ''}`} onClick={() => setBlackEngine('katagui')}>KataGUI</button>
            </div>
          </div>

          <div className="battle-setup-section">
            <h3>White</h3>
            <div className="battle-engine-toggle">
              <button className="battle-engine-btn active" disabled>{engineLabel(whiteEngine, kataguiModel)}</button>
            </div>
            <p className="battle-setup-note">Auto-assigned</p>
          </div>

          <div className="battle-setup-section">
            <h3>KataGUI Model</h3>
            <div className="battle-engine-toggle">
              <button className={`battle-engine-btn${kataguiModel === 'guest' ? ' active' : ''}`} onClick={() => setKataguiModel('guest')}>Guest (b10)</button>
              <button className={`battle-engine-btn${kataguiModel === 'pro'   ? ' active' : ''}`} onClick={() => setKataguiModel('pro')}>Pro (b18)</button>
            </div>
            {kataguiModel === 'pro' && <p className="battle-setup-note">Requires KataGUI login in server_config.ini</p>}
          </div>

          <div className="battle-setup-section">
            <h3>Board &amp; Komi</h3>
            <div className="battle-board-row">
              <select className="battle-select" value={boardSize} onChange={e => setBoardSize(Number(e.target.value))}>
                <option value={19}>19×19</option>
                <option value={13}>13×13</option>
                <option value={9}>9×9</option>
              </select>
              <input className="battle-komi-input" type="number" step="0.5"
                value={komi} onChange={e => setKomi(Number(e.target.value))} />
              <label>komi</label>
            </div>
          </div>
        </div>

        <div className="battle-modal-footer">
          {canCancel && (
            <button className="battle-cancel-btn" onClick={onCancel}>Cancel</button>
          )}
          <button className="battle-start-btn" onClick={() => onStart({ blackEngine, kataguiModel, boardSize, komi })}>
            Start Battle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BattlePage() {
  const [battle,    setBattle]    = useState<BattleState | null>(null);
  const [showModal, setShowModal] = useState(true);
  const [error,     setError]     = useState('');
  const [viewMove,  setViewMove]  = useState<number | null>(null); // null = follow latest

  const pollRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const moveListRef = useRef<HTMLDivElement>(null);
  const rowRefs    = useRef<Record<number, HTMLTableRowElement | null>>({});

  const whiteEngine: Engine = battle
    ? (battle.config.blackEngine === 'deepgo' ? 'katagui' : 'deepgo')
    : 'katagui';

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((battleId: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const state = await api.getBattle(battleId);
        setBattle(state);
        if (state.status !== 'running') stopPolling();
      } catch (e) { console.error(e); }
    }, 800);
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Auto-scroll move list to bottom only when following latest
  useEffect(() => {
    if (viewMove === null && moveListRef.current) {
      moveListRef.current.scrollTop = moveListRef.current.scrollHeight;
    }
  }, [battle?.moves.length, viewMove]);

  const startBattle = async (cfg: { blackEngine: Engine; kataguiModel: KataguiModel; boardSize: number; komi: number }) => {
    stopPolling();
    setBattle(null);
    setError('');
    setViewMove(null);
    setShowModal(false);
    try {
      const { battle_id } = await api.startBattle(cfg);
      startPolling(battle_id);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const pauseBattle = async () => {
    if (!battle) return;
    console.log('[battle] pausing battle', battle.id);
    stopPolling();
    try {
      const r = await api.pauseBattle(battle.id);
      console.log('[battle] pause response', r);
      setBattle(prev => prev ? { ...prev, status: 'paused', result: 'Paused', thinking: null } : prev);
    } catch (e: any) {
      console.error('[battle] pause failed:', e);
      setError(e.message ?? 'Pause failed — try restarting the gateway');
    }
  };

  const resumeBattle = async () => {
    if (!battle) return;
    setViewMove(null);
    try {
      await api.resumeBattle(battle.id);
      startPolling(battle.id);
      setBattle(prev => prev ? { ...prev, status: 'running', result: null } : prev);
    } catch (e: any) {
      console.error('Resume failed:', e);
      setError(e.message ?? 'Resume failed');
    }
  };

  const handleSave = useCallback(() => {
    if (!battle || battle.moves.length === 0) return;
    const sgfMoves = battle.moves.map(m => ({
      mv: m.move, p: m.winprob ?? undefined, score: m.score ?? undefined, agent: 'bot' as const,
    }));
    const blackLabel = engineLabel(battle.config.blackEngine, battle.config.kataguiModel);
    const whiteLabel = engineLabel(whiteEngine, battle.config.kataguiModel);
    const sgf = moves2sgf(sgfMoves, {
      pb: blackLabel, pw: whiteLabel,
      km: String(battle.config.komi),
      re: battle.result ?? '',
      dt: new Date().toISOString().slice(0, 10),
    });
    downloadSgf(`battle-${new Date().toISOString().slice(0, 10)}.sgf`, sgf);
  }, [battle, whiteEngine]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const totalMoves = battle?.moves.length ?? 0;
  const viewPos    = viewMove ?? totalMoves;

  const goFirst  = useCallback(() => setViewMove(1), []);
  const goPrev   = useCallback(() => setViewMove(v => Math.max(1, (v ?? totalMoves) - 1)), [totalMoves]);
  const goPrevN  = useCallback((n: number) => setViewMove(v => Math.max(1, (v ?? totalMoves) - n)), [totalMoves]);
  const goNext   = useCallback(() => setViewMove(v => { const next = (v ?? totalMoves) + 1; return next >= totalMoves ? null : next; }), [totalMoves]);
  const goNextN  = useCallback((n: number) => setViewMove(v => { const next = (v ?? totalMoves) + n; return next >= totalMoves ? null : next; }), [totalMoves]);
  const goLast   = useCallback(() => setViewMove(null), []);

  // ── Derived board state ──────────────────────────────────────────────────────
  const bSize = battle?.config.boardSize ?? 19;

  const sgfMoves = useMemo((): Move[] => {
    if (!battle) return [];
    return battle.moves.map(m => ({ mv: m.move, agent: 'bot' as const }));
  }, [battle?.moves]);

  const stones = useMemo(() => {
    if (!battle) return new Map();
    return computeBoardState(sgfMoves, bSize, viewPos);
  }, [sgfMoves, bSize, viewPos]);

  const marks = useMemo((): BoardMark[] => {
    if (!battle || viewPos === 0) return [];
    const m = battle.moves[viewPos - 1];
    if (!m?.move || m.move.toLowerCase() === 'pass' || m.move.toLowerCase() === 'resign') return [];
    const pt = sgfToPoint(m.move, bSize);
    if (!pt) return [];
    return [{ coord: pt, type: 'circle' as const }];
  }, [battle?.moves, viewPos, bSize]);

  const chartPoints = useMemo((): WinratePoint[] =>
    (battle?.moves ?? [])
      .filter(m => m.winprob !== null)
      .map(m => ({ moveNum: m.num, winprob: m.winprob!, score: m.score ?? undefined, move: m.move, engine: m.engine })),
    [battle?.moves]);

  const handleChartClick = useCallback((moveNum: number) => {
    if (battle?.status === 'running') return;
    const isLatest = moveNum === battle?.moves.length;
    setViewMove(isLatest ? null : moveNum);
    const row = rowRefs.current[moveNum];
    if (row) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [battle?.status, battle?.moves.length]);

  const isRunning  = battle?.status === 'running';
  const isPaused   = battle?.status === 'paused';
  const navDisabled = isRunning;

  return (
    <div className="battle-page">
      {/* Modal */}
      {showModal && (
        <SetupModal
          onStart={startBattle}
          onCancel={() => setShowModal(false)}
          canCancel={!!battle}
        />
      )}

      {/* Header */}
      <div className="battle-header">
        <h1 className="battle-title">Bot vs Bot</h1>
        {battle && (
          <div className="battle-players">
            <span className="battle-player">⚫ {engineLabel(battle.config.blackEngine, battle.config.kataguiModel)}</span>
            <span className="battle-vs">vs</span>
            <span className="battle-player">⚪ {engineLabel(whiteEngine, battle.config.kataguiModel)}</span>
            <span className="battle-meta">{bSize}×{bSize} · {battle.config.komi} komi</span>
          </div>
        )}
        <div className="battle-header-right">
          {battle && battle.moves.length > 0 &&
            <button className="battle-stop-btn" onClick={handleSave}>Save SGF</button>
          }
          {isRunning && (
            <button className="battle-stop-btn" onClick={pauseBattle}>Pause</button>
          )}
          {isPaused && (
            <button className="battle-stop-btn battle-resume-btn" onClick={resumeBattle}>Resume</button>
          )}
          <button className="battle-stop-btn" onClick={() => setShowModal(true)}
            disabled={isRunning}>
            New Battle
          </button>
        </div>
      </div>

      {error && <div className="battle-error">{error}</div>}

      {/* Status bar — only when a battle exists */}
      {battle && (
        <div className="battle-status-bar">
          {isRunning
            ? <span className="battle-thinking">⏳ {battle.thinking ?? '…'} is thinking</span>
            : <span className="battle-result">{battle.result ?? battle.error}</span>
          }
          <span className="battle-move-count">{battle.moves.length} moves</span>
        </div>
      )}

      {/* Empty state */}
      {!battle && (
        <div className="battle-empty">
          <p>Click <strong>New Battle</strong> to configure and start a bot-vs-bot game.</p>
        </div>
      )}

      {/* Board + chart + move list */}
      {battle && (
        <div className="battle-content">
          {/* Board + nav */}
          <div className="battle-board-wrap">
            <GoBoard
              size={bSize}
              stones={stones}
              marks={marks}
              showHover={false}
              width={480}
              height={480}
            />
            <div className="battle-nav">
              <div className="btn-icon-group">
                <button onClick={goFirst}
                  disabled={navDisabled || viewPos <= 1}
                  title="First move"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewPos <= 1) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewPos <= 1) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faBackwardStep} size="lg" />
                </button>
                <button onClick={() => goPrevN(10)}
                  disabled={navDisabled || viewPos <= 1}
                  title="Back 10"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewPos <= 1) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewPos <= 1) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faBackward} size="lg" />
                </button>
                <button onClick={goPrev}
                  disabled={navDisabled || viewPos <= 1}
                  title="Previous move"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewPos <= 1) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewPos <= 1) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                </button>
                <button onClick={goNext}
                  disabled={navDisabled || viewPos >= totalMoves}
                  title="Next move"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewPos >= totalMoves) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewPos >= totalMoves) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faChevronRight} size="lg" />
                </button>
                <button onClick={() => goNextN(10)}
                  disabled={navDisabled || viewPos >= totalMoves}
                  title="Forward 10"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewPos >= totalMoves) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewPos >= totalMoves) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faForward} size="lg" />
                </button>
                <button onClick={goLast}
                  disabled={navDisabled || viewMove === null}
                  title="Latest move"
                  style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', padding: '3px 5px', cursor: (navDisabled || viewMove === null) ? 'not-allowed' : 'pointer', borderRadius: '4px', display: 'flex', alignItems: 'center', color: (navDisabled || viewMove === null) ? '#BBB199' : '#333' }}>
                  <FontAwesomeIcon icon={faForwardStep} size="lg" />
                </button>
              </div>
              <span className="battle-nav-label">{viewPos} / {totalMoves}</span>
            </div>
          </div>

          {/* Right column: chart + move list */}
          <div className="battle-right-col">
            <WinrateChart
              points={chartPoints}
              currentMove={viewPos > 0 ? battle.moves[viewPos - 1]?.num : undefined}
              onMoveClick={handleChartClick}
              height={140}
            />

            <div className="battle-movelist" ref={moveListRef}>
              <table className="battle-table">
                <thead>
                  <tr>
                    <th>#</th><th>Color</th><th>Engine</th><th>Move</th>
                    <th>Score</th><th>Win%</th><th>Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {battle.moves.map(m => (
                    <tr key={m.num}
                      ref={el => { rowRefs.current[m.num] = el; }}
                      className={[
                        viewMove === null && m.num === totalMoves ? 'latest' : '',
                        viewMove !== null && m.num === viewMove   ? 'chart-selected' : '',
                      ].join(' ').trim()}>
                      <td>{m.num}</td>
                      <td>{m.color === 'black' ? '⚫' : '⚪'}</td>
                      <td className="battle-td-engine">{m.engine}</td>
                      <td className="battle-td-move">{m.move}</td>
                      <td className={`battle-td-score ${m.score === null ? '' : m.score > 0 ? 'score-black' : m.score < 0 ? 'score-white' : ''}`}>
                        {m.score !== null ? formatScore(m.score, battle.config.komi) : '—'}
                      </td>
                      <td className="battle-td-win">
                        {m.winprob !== null ? (m.winprob * 100).toFixed(1) + '%' : '—'}
                      </td>
                      <td className="battle-td-visits">{m.visits ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
